import { CATEGORY_TITLES, formatContact } from '../../utils/resumeFormat';

// Live, instant HTML mirror of the generated .docx. It shows the exact same
// structured content and spec ordering (§6) so the applicant sees the real
// resume take shape without a server round-trip. The downloadable file is a
// true Word document built by utils/resumeDocx.

const Heading = ({ children }) => (
  <h2 className="mt-3 mb-0.5 text-[15px] font-bold text-[#9a5b16]">{children}</h2>
);

// "years \t details" info line: years pinned right, details flowing after.
const TabLine = ({ leading, children }) => (
  <p className="flex gap-3 text-[11px] leading-4 text-[#262626]">
    <span className="shrink-0 font-normal">{leading}</span>
    <span>{children}</span>
  </p>
);

const ResumePreview = ({ model, identity }) => {
  const contact = formatContact(identity);
  const fullName = String(identity?.fullName || '').trim();

  return (
    <div dir="rtl" className="min-h-[420px] bg-white px-8 py-6 text-right font-sans text-[#262626]">
      {fullName && <h1 className="text-center text-[18px] font-bold text-[#9a5b16]">{fullName}</h1>}
      {contact && <p className="mt-1 text-center text-[11px] text-gray-600">{contact}</p>}

      {model.summary && (
        <section>
          <p className="text-[11px] leading-4">{model.summary}</p>
        </section>
      )}

      {model.education.length > 0 && (
        <section>
          <Heading>{CATEGORY_TITLES.education}</Heading>
          {model.education.map((entry, index) => (
            <TabLine key={index} leading={entry.years}>{entry.details}</TabLine>
          ))}
        </section>
      )}

      {model.experience.length > 0 && (
        <section>
          <Heading>{CATEGORY_TITLES.experience}</Heading>
          {model.experience.map((entry, index) => (
            <div key={index} className="mb-1.5">
              <p className="flex gap-3 text-[11px] leading-4">
                <span className="shrink-0">{entry.period}</span>
                <span>
                  <strong>{entry.company}</strong>
                  {entry.company && entry.role ? ' | ' : ''}
                  {entry.role}
                </span>
              </p>
              {entry.bullets.map((bullet, bulletIndex) => (
                <p key={bulletIndex} className="flex gap-1 pr-2 text-[11px] leading-4">
                  <span aria-hidden="true">•</span>
                  <span>{bullet}</span>
                </p>
              ))}
            </div>
          ))}
        </section>
      )}

      {model.relevantSkills.length > 0 && (
        <section>
          <Heading>{CATEGORY_TITLES.relevantSkills}</Heading>
          {model.relevantSkills.map((entry, index) => (
            <p key={index} className="mb-1 text-[11px] leading-4">
              <strong>{entry.title}</strong>: {entry.description}
            </p>
          ))}
        </section>
      )}

      {model.languages && (
        <section>
          <Heading>{CATEGORY_TITLES.languages}</Heading>
          <p className="text-[11px] leading-4">{model.languages}</p>
        </section>
      )}

      {model.militaryService.length > 0 && (
        <section>
          <Heading>{CATEGORY_TITLES.militaryService}</Heading>
          {model.militaryService.map((entry, index) => (
            <TabLine key={index} leading={entry.years}>{entry.detail}</TabLine>
          ))}
        </section>
      )}

      {model.computerSkills && (
        <section>
          <Heading>{CATEGORY_TITLES.computerSkills}</Heading>
          <p className="text-[11px] leading-4">{model.computerSkills}</p>
        </section>
      )}

      {model.courses.length > 0 && (
        <section>
          <Heading>{CATEGORY_TITLES.courses}</Heading>
          {model.courses.map((entry, index) => (
            <TabLine key={index} leading={entry.year}>{entry.name}</TabLine>
          ))}
        </section>
      )}
    </div>
  );
};

export default ResumePreview;
